import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import GameCard from "../components/games/GameCard";
import { getUserFavouriteGames } from "../api/games";
import { useAuth } from "../AuthContext";
import { useFavorites } from "../context/FavoritesContext";
import Button from "../components/ui/Button";

const GameCardSkeleton = () => (
  <div className="rounded-3xl border border-white/10 bg-surface-card/60 backdrop-blur-md shadow-card overflow-hidden">
    <div className="h-[200px] gg-shimmer" />
    <div className="p-4 space-y-2.5">
      <div className="h-6 rounded gg-shimmer" />
      <div className="h-4 w-2/3 rounded gg-shimmer" />
      <div className="flex gap-2 mt-2">
        <div className="h-6 w-14 rounded-full gg-shimmer" />
        <div className="h-6 w-20 rounded-full gg-shimmer" />
      </div>
    </div>
  </div>
);

const gridClass =
  "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 w-full max-w-[1400px] mx-auto";

export default function FavoritesPage() {
  const { user } = useAuth();
  const { favorites: favoriteIds } = useFavorites(); // Get favorites from context for reactivity
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    if (user) {
      getUserFavouriteGames().then((data) => {
        setFavorites(data);
        setLoading(false);
      });
    } else {
      setFavorites([]);
      setLoading(false);
    }
  }, [user, favoriteIds]); // Re-load when favorites change

  return (
    <div className="min-h-[calc(100vh-var(--header-height))] py-8 sm:py-10 px-3 sm:px-6 md:px-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-surface-card/40 backdrop-blur-md mb-8 px-6 sm:px-10 py-9 sm:py-12">
          <div className="absolute inset-0 gg-grid-backdrop opacity-40" />
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-56 w-[70%] rounded-full bg-red-500/20 blur-3xl animate-pulseGlow" />
          <div className="relative text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold text-brand-gold-soft backdrop-blur-sm mb-4">
              <span className="text-sm">❤️</span> Your collection
            </span>
            <h1 className="font-heading font-extrabold text-white text-4xl sm:text-5xl md:text-6xl tracking-tight">
              Your <span className="gg-gradient-text">Favorites</span>
            </h1>
            <p className="text-white/65 text-sm sm:text-base mt-3 max-w-xl mx-auto">
              {user && favorites.length > 0
                ? `${favorites.length} game${favorites.length > 1 ? "s" : ""} you've saved, all in one place.`
                : "Every game you save lives here, ready when you are."}
            </p>
          </div>
        </div>

        {loading ? (
          <div className={gridClass}>
            {[...Array(8)].map((_, i) => (
              <GameCardSkeleton key={i} />
            ))}
          </div>
        ) : favorites.length > 0 ? (
          <div className={`${gridClass} animate-fadeIn`}>
            {favorites.map((game, idx) => (
              <div
                key={game.id}
                style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
                className="animate-fadeUp"
              >
                <GameCard game={game} />
              </div>
            ))}
          </div>
        ) : !user ? (
          <div className="max-w-xl mx-auto rounded-3xl border border-white/10 bg-surface-card/40 backdrop-blur-md px-6 py-14 text-center animate-fadeUp">
            <div className="text-5xl mb-4">🔒</div>
            <h2 className="font-heading font-bold text-2xl text-white mb-2">
              Sign in to see your favorites
            </h2>
            <p className="text-white/55 mb-6 max-w-sm mx-auto">
              Log in to save games and keep your collection in sync across devices.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button variant="primary" size="lg" onClick={() => navigate("/auth/signin")}>
                Sign in
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate("/auth/signup")}>
                Create account
              </Button>
            </div>
          </div>
        ) : (
          <div className="max-w-xl mx-auto rounded-3xl border border-white/10 bg-surface-card/40 backdrop-blur-md px-6 py-14 text-center animate-fadeUp">
            <div className="text-5xl mb-4">💔</div>
            <h2 className="font-heading font-bold text-2xl text-white mb-2">
              No favorites yet
            </h2>
            <p className="text-white/55 mb-6 max-w-sm mx-auto">
              Tap the heart on any game to add it here. Start exploring to build your
              collection.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button variant="primary" size="lg" onClick={() => navigate("/games")}>
                Browse games
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate("/")}>
                Find games
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
